import { useRouter } from '@tanstack/react-router';
import { mapApiErrorToForm } from '@upme/api-client';
import {
  type AdminMeetingTemplateDto,
  type CityDto,
  type MeetingTemplateCreateInput,
  type MeetingTemplateUpdateInput,
  MeetingTemplateCreateInput as MTCreate,
  MeetingTemplateUpdateInput as MTUpdate,
} from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Checkbox } from '@upme/ui/components/checkbox';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Switch } from '@upme/ui/components/switch';
import { useMemo } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { collectRootMessages } from '../../../app/form-errors';
import { zodResolver } from '../../../app/zod-resolver';
import { useCreateMeetingTemplate, useUpdateMeetingTemplate } from '../mutations';

type CreateProps = { mode: 'create'; cities: ReadonlyArray<CityDto> };
type EditProps = { mode: 'edit'; template: AdminMeetingTemplateDto; cities: ReadonlyArray<CityDto> };
type Props = CreateProps | EditProps;

type FormValues = MeetingTemplateCreateInput;

export function MeetingTemplateForm(props: Props) {
  const router = useRouter({ warn: false });
  const isCreate = props.mode === 'create';
  const schema = isCreate ? MTCreate : MTUpdate.loose();

  const defaults: Partial<FormValues> = isCreate
    ? ({ sortOrder: 0, isActive: true, allCities: false, cityIds: [] } as Partial<FormValues>)
    : {
        slug: props.template.slug,
        emoji: props.template.emoji,
        title: props.template.title,
        description: props.template.description ?? undefined,
        defaultTime: props.template.defaultTime ?? undefined,
        defaultDurationMinutes: props.template.defaultDurationMinutes ?? undefined,
        sortOrder: props.template.sortOrder,
        isActive: props.template.isActive,
        allCities: props.template.allCities,
        cityIds: props.template.cityIds,
      };

  const form = useForm<FormValues>({
    resolver: zodResolver(schema as unknown as z.ZodType<FormValues>) as Resolver<FormValues>,
    defaultValues: defaults as FormValues,
    mode: 'onSubmit',
  });

  const create = useCreateMeetingTemplate();
  const update = useUpdateMeetingTemplate(isCreate ? '' : props.template.id);

  const allCities = form.watch('allCities');
  const selectedCityIds = form.watch('cityIds') ?? [];
  const selected = useMemo(() => new Set(selectedCityIds), [selectedCityIds]);

  const toggleCity = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    form.setValue('cityIds', Array.from(next));
  };

  const visibleCities = props.cities.filter((c) => c.isActive || selected.has(c.id));

  const onSubmit = form.handleSubmit(async (values) => {
    const cityIds = values.allCities ? [] : values.cityIds;
    try {
      let result: AdminMeetingTemplateDto;
      if (isCreate) {
        const body: MeetingTemplateCreateInput = { ...values, cityIds };
        result = await create.mutateAsync(body);
      } else {
        const body: MeetingTemplateUpdateInput = {
          emoji: values.emoji,
          title: values.title,
          description: values.description ?? null,
          defaultTime: values.defaultTime ?? null,
          defaultDurationMinutes: values.defaultDurationMinutes ?? null,
          sortOrder: values.sortOrder,
          isActive: values.isActive,
          allCities: values.allCities,
          cityIds,
        };
        result = await update.mutateAsync(body);
      }
      toast.success(isCreate ? 'Шаблон создан' : 'Изменения сохранены');
      router?.navigate({ to: '/meeting-templates/$templateId', params: { templateId: result.id } });
    } catch (err) {
      await mapApiErrorToForm(err, form, { rootField: 'slug' });
    }
  });

  const slugError = form.formState.errors.slug;
  const emojiError = form.formState.errors.emoji;
  const titleError = form.formState.errors.title;
  const descriptionError = form.formState.errors.description;
  const defaultTimeError = form.formState.errors.defaultTime;
  const defaultDurationError = form.formState.errors.defaultDurationMinutes;
  const sortOrderError = form.formState.errors.sortOrder;
  const rootMessages = collectRootMessages(form.formState.errors as Record<string, unknown>);

  return (
    <form onSubmit={onSubmit} className="space-y-4 max-w-xl" noValidate>
      <div>
        <Label htmlFor="slug">Slug</Label>
        <Input id="slug" {...form.register('slug')} disabled={!isCreate} placeholder="coffee-walk" />
        {slugError && <p className="text-sm text-destructive mt-1">{String(slugError.message)}</p>}
        <p className="text-xs text-muted-foreground mt-1">lowercase, цифры, дефисы. Менять нельзя.</p>
      </div>

      <div>
        <Label htmlFor="emoji">Эмодзи</Label>
        <Input id="emoji" {...form.register('emoji')} placeholder="☕" maxLength={8} />
        {emojiError && <p className="text-sm text-destructive mt-1">{String(emojiError.message)}</p>}
      </div>

      <div>
        <Label htmlFor="title">Название</Label>
        <Input id="title" {...form.register('title')} placeholder="Кофе-встреча" />
        {titleError && <p className="text-sm text-destructive mt-1">{String(titleError.message)}</p>}
      </div>

      <div>
        <Label htmlFor="description">Описание</Label>
        <textarea
          id="description"
          rows={3}
          className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
          {...form.register('description')}
        />
        {descriptionError && (
          <p className="text-sm text-destructive mt-1">{String(descriptionError.message)}</p>
        )}
      </div>

      <div>
        <Label htmlFor="defaultTime">Время по умолчанию</Label>
        <Input id="defaultTime" type="time" {...form.register('defaultTime')} />
        {defaultTimeError && (
          <p className="text-sm text-destructive mt-1">{String(defaultTimeError.message)}</p>
        )}
      </div>

      <div>
        <Label htmlFor="defaultDurationMinutes">Длительность (мин)</Label>
        <Input
          id="defaultDurationMinutes"
          type="number"
          min={5}
          max={1440}
          {...form.register('defaultDurationMinutes', { valueAsNumber: true })}
        />
        {defaultDurationError && (
          <p className="text-sm text-destructive mt-1">{String(defaultDurationError.message)}</p>
        )}
      </div>

      <div>
        <Label htmlFor="sortOrder">Порядок сортировки</Label>
        <Input
          id="sortOrder"
          type="number"
          min={0}
          max={10000}
          {...form.register('sortOrder', { valueAsNumber: true })}
        />
        {sortOrderError && <p className="text-sm text-destructive mt-1">{String(sortOrderError.message)}</p>}
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="isActive"
          checked={form.watch('isActive') ?? true}
          onCheckedChange={(v) => form.setValue('isActive', v)}
        />
        <Label htmlFor="isActive">Активен</Label>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="allCities"
          checked={allCities ?? false}
          onCheckedChange={(v) => form.setValue('allCities', v)}
        />
        <Label htmlFor="allCities">Все города</Label>
      </div>

      {!allCities && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Города</p>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {visibleCities.map((c) => (
              <div key={c.id} className="flex items-center gap-2">
                <Checkbox
                  id={`city-${c.id}`}
                  checked={selected.has(c.id)}
                  onCheckedChange={() => toggleCity(c.id)}
                />
                <Label htmlFor={`city-${c.id}`}>{c.name}</Label>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Выбрано {selected.size}</p>
        </div>
      )}

      {rootMessages.map((m) => (
        <p key={m} className="text-sm text-destructive">
          {m}
        </p>
      ))}

      <Button type="submit" disabled={form.formState.isSubmitting}>
        {isCreate ? 'Создать' : 'Сохранить'}
      </Button>
    </form>
  );
}
